<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import LabelItem from 'dashboard/components-next/Label/LabelItem.vue';
import AddLabel from 'dashboard/components-next/Label/AddLabel.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const store = useStore();
const route = useRoute();

const showDropdown = ref(false);

// Store the currently hovered label's ID
// Using JS state management instead of CSS :hover / group hover
// This will solve the flickering issue when hovering over the last label item
const hoveredLabel = ref(null);

const allLabels = useMapGetter('labels/getLabels');
const contactLabels = useMapGetter('contactLabels/getContactLabels');

const normalizeTitle = title =>
  String(title || '')
    .trim()
    .toLowerCase();

const savedLabels = computed(() => {
  const availableContactLabels = contactLabels.value(props.contactId);
  if (!Array.isArray(availableContactLabels)) {
    return [];
  }

  return availableContactLabels.map(title => {
    const match = allLabels.value.find(
      label => normalizeTitle(label.title) === normalizeTitle(title)
    );

    if (match) {
      return match;
    }

    return {
      id: `tag-${normalizeTitle(title)}`,
      title,
      color: '#1f93ff',
    };
  });
});

const labelMenuItems = computed(() => {
  return allLabels.value
    ?.map(label => ({
      label: label.title,
      value: label.id,
      thumbnail: { name: label.title, color: label.color },
      isSelected: savedLabels.value.some(
        savedLabel => savedLabel.id === label.id
      ),
      action: 'contactLabel',
    }))
    .toSorted((a, b) => Number(a.isSelected) - Number(b.isSelected));
});

const fetchLabels = async contactId => {
  if (!contactId) {
    return;
  }
  store.dispatch('contactLabels/get', contactId);
};

const handleLabelAction = async ({ value }) => {
  try {
    const selectedLabel = allLabels.value.find(label => label.id === value);
    const fallbackLabel = savedLabels.value.find(label => label.id === value);
    const titleToToggle = selectedLabel?.title || fallbackLabel?.title;
    if (!titleToToggle) return;

    const currentLabels = savedLabels.value.map(label => label.title);
    const isSelected = currentLabels.some(
      labelTitle => normalizeTitle(labelTitle) === normalizeTitle(titleToToggle)
    );

    const updatedLabels = isSelected
      ? currentLabels.filter(
          labelTitle =>
            normalizeTitle(labelTitle) !== normalizeTitle(titleToToggle)
        )
      : [...currentLabels, titleToToggle];

    await store.dispatch('contactLabels/update', {
      contactId: props.contactId,
      labels: updatedLabels,
    });

    showDropdown.value = false;
  } catch (error) {
    // error
  }
};

const handleRemoveLabel = label => {
  return handleLabelAction({ value: label.id });
};

watch(
  () => props.contactId,
  newVal => {
    fetchLabels(newVal || route.params.contactId);
  },
  { immediate: true }
);
onMounted(() => {
  if (!allLabels.value.length) {
    store.dispatch('labels/get');
  }
});

const handleMouseLeave = () => {
  // Reset hover state when mouse leaves the container
  // This ensures all labels return to their default state
  hoveredLabel.value = null;
};

const handleLabelHover = labelId => {
  // Added this to prevent flickering on when showing remove button on hover
  // If the label item is at end of the line, it will show the remove button
  // when hovering over the last label item
  hoveredLabel.value = labelId;
};
</script>

<template>
  <div class="flex flex-wrap items-center gap-2" @mouseleave="handleMouseLeave">
    <LabelItem
      v-for="label in savedLabels"
      :key="label.id"
      :label="label"
      :is-hovered="hoveredLabel === label.id"
      @remove="handleRemoveLabel"
      @hover="handleLabelHover(label.id)"
    />
    <AddLabel
      :label-menu-items="labelMenuItems"
      @update-label="handleLabelAction"
    />
  </div>
</template>
